import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

export interface User {
  id: string;
  email: string;
  display_name?: string;
}

interface AuthState {
  token: string | null;
  user: User | null;
  ready: boolean;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<{ user: User }>;
  register: (email: string, password: string, displayName?: string) => Promise<{ user: User }>;
  logout: () => void;
  setAuthFromToken: (token: string, user: User) => void;
  onUnauthorized: () => void;
}

const STORAGE_KEY = 'test_data_hub_token';
const USER_KEY = 'test_data_hub_user';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    token: null,
    user: null,
    ready: false,
  });

  useEffect(() => {
    const token = localStorage.getItem(STORAGE_KEY);
    const userStr = localStorage.getItem(USER_KEY);
    let user: User | null = null;
    if (userStr) {
      try {
        user = JSON.parse(userStr) as User;
      } catch (_) {}
    }
    setState({ token, user, ready: true });
  }, []);

  const setAuth = useCallback((token: string | null, user: User | null) => {
    if (token) {
      localStorage.setItem(STORAGE_KEY, token);
      localStorage.setItem(USER_KEY, user ? JSON.stringify(user) : '');
    } else {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(USER_KEY);
    }
    setState(s => ({ ...s, token, user }));
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      const { getApiBaseUrl } = await import('../config/config');
      const res = await fetch(`${getApiBaseUrl()}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Login failed');
      const { access_token, user: u } = data;
      setAuth(access_token, u);
      return { user: u };
    },
    [setAuth]
  );

  const register = useCallback(
    async (email: string, password: string, displayName?: string) => {
      const { getApiBaseUrl } = await import('../config/config');
      const res = await fetch(`${getApiBaseUrl()}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, display_name: displayName || '' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Registration failed');
      const { access_token, user: u } = data;
      setAuth(access_token, u);
      return { user: u };
    },
    [setAuth]
  );

  const logout = useCallback(() => {
    setAuth(null, null);
  }, [setAuth]);

  const setAuthFromToken = useCallback(
    (token: string, user: User) => {
      setAuth(token, user);
    },
    [setAuth]
  );

  const onUnauthorized = useCallback(() => {
    setAuth(null, null);
    window.location.href = '/login';
  }, [setAuth]);

  useEffect(() => {
    const handler = () => onUnauthorized();
    window.addEventListener('auth:unauthorized', handler);
    return () => window.removeEventListener('auth:unauthorized', handler);
  }, [onUnauthorized]);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        register,
        logout,
        setAuthFromToken,
        onUnauthorized,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function getStoredToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(STORAGE_KEY);
}
